import ReactPixel from 'react-facebook-pixel';

export interface FBPixelEvent {
  eventName: string;
  parameters?: Record<string, any>;
}

export interface FBEventOptions {
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
  [key: string]: any;
}

export class FBAnalytics {
  private static instance: FBAnalytics;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): FBAnalytics {
    if (!FBAnalytics.instance) {
      FBAnalytics.instance = new FBAnalytics();
    }
    return FBAnalytics.instance;
  }

  /**
   * Initialize Facebook Pixel
   */
  public init(pixelId: string, options?: any): void {
    if (!this.isInitialized) {
      ReactPixel.init(pixelId, options);
      this.isInitialized = true;
    }
  }

  /**
   * Track a custom event
   */
  public track(eventName: string, parameters?: Record<string, any>): void {
    try {
      if (parameters) {
        ReactPixel.track(eventName, parameters);
      } else {
        ReactPixel.track(eventName);
      }
    } catch (error) {
      console.error('Error tracking Facebook pixel event:', error);
    }
  }

  /**
   * Track subscription event
   */
  public trackSubscribe(options?: FBEventOptions): void {
    const defaultOptions: FBEventOptions = {
      content_name: 'Poshwatch Subscription',
      content_category: 'subscription',
      value: 29.99,
      currency: 'USD',
      ...options
    };
    
    this.track('Subscribe', defaultOptions);
  }

  /**
   * Track trial start event
   */
  public trackStartTrial(options?: FBEventOptions): void {
    const defaultOptions: FBEventOptions = {
      content_name: 'Poshwatch Free Trial',
      content_category: 'trial',
      value: 0,
      currency: 'USD',
      ...options
    };
    
    this.track('StartTrial', defaultOptions);
  }

  /**
   * Track page view event
   */
  public trackPageView(options?: FBEventOptions): void {
    this.track('PageView', options);
  }

  /**
   * Track view content event
   */
  public trackViewContent(options?: FBEventOptions): void {
    const defaultOptions: FBEventOptions = {
      content_category: 'content',
      ...options
    };
    
    this.track('ViewContent', defaultOptions);
  }

  /**
   * Track complete registration event
   */
  public trackCompleteRegistration(options?: FBEventOptions): void {
    const defaultOptions: FBEventOptions = {
      content_category: 'registration',
      ...options
    };
    
    this.track('CompleteRegistration', defaultOptions);
  }

  /**
   * Track event once per session (with deduplication)
   */
  public trackOnce(eventName: string, options?: FBEventOptions, sessionKey?: string): void {
    const key = sessionKey || `fb_${eventName}_tracked`;
    const hasTracked = localStorage.getItem(key);
    
    if (!hasTracked) {
      this.track(eventName, options);
      localStorage.setItem(key, 'true');
    }
  }

  /**
   * Track subscription once per session
   */
  public trackSubscribeOnce(options?: FBEventOptions): void {
    this.trackOnce('Subscribe', options, 'poshwatch_subscribe_tracked');
  }

  /**
   * Clear tracking session data
   */
  public clearSessionTracking(): void {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('fb_') && key.endsWith('_tracked')) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Get pixel instance for advanced usage
   */
  public getPixelInstance(): any {
    return ReactPixel;
  }
}

// Export singleton instance
export const fbAnalytics = FBAnalytics.getInstance();
